import { useMemo } from 'react';
import { useAuthQuery } from '../../features/common/apiUtils/queryHelper';
import config from 'features/common/config/config';
import { FlagsProvider } from './features';
import { LoadingScreen } from 'features/common/components/LoadingScreen';
import { shouldEnableFlag } from './azure-feature-flags-conditions';
import { GetUserOrgDepsQuery } from 'gql/graphql';

// Type representing the organization from GetUserOrgDepsQuery.
type OrganizationFromQuery = NonNullable<GetUserOrgDepsQuery['organization']>;

/**
 * Azure Feature Flag Value
 */
export interface AzureFeatureFlagValue {
  id: string;
  enabled: boolean;
  conditions: {
    client_filters: { name: string; parameters?: Record<string, string> }[];
  };
  description?: string;
  displayName?: string;
}

/**
 * Azure Feature Flags Hook, returns the Azure Feature Flags response.
 *
 * NOTE: Must be used within an authenticated context.
 * The API call requires a valid auth token to succeed.
 *
 * @param options
 * @returns {UseQueryResult<AzureFeatureFlagValue[], unknown>}
 */
export function useAzureFeatureFlags(options?: Partial<{ enabled: boolean }>) {
  return useAuthQuery(
    config.featureFlags.scope,
    (axiosInstance) =>
      axiosInstance?.get<AzureFeatureFlagValue[]>(config.featureFlags.url),
    {
      enabled: options?.enabled ?? true,
      staleTime: 3 * 60 * 1000, // 3 mins
      cacheTime: 10 * 60 * 1000, // 10 mins
    },
  );
}

/**
 * Transforms the Azure Feature Flag response into a format that is compatible with the `FlagsProvider` component.
 * @param flags
 * @returns {Record<string, boolean>}
 */

function transformFlags({
  flags,
  data,
}: {
  flags: AzureFeatureFlagValue[];
  data: { organization?: OrganizationFromQuery | null };
}) {
  const result: Record<string, boolean> = {};

  for (const flag of flags) {
    result[flag.id] = shouldEnableFlag({ flag, data });
  }

  return result;
}

type AzureFeatureProviderProps = {
  children: React.ReactNode;
  data: OrganizationFromQuery | null | undefined;
  isOrgDataLoading?: boolean;
};

/**
 * Azure Feature Flags Provider
 * @param children
 * @returns {JSX.Element}
 */
export function AzureFeaturesProvider({
  children,
  ...props
}: AzureFeatureProviderProps) {
  const { data, isLoading } = useAzureFeatureFlags();
  const togglePoints = useMemo(
    () =>
      transformFlags({
        flags: data?.data ?? [],
        data: { organization: props.data },
      }),
    [data, props.data],
  );

  if (isLoading || props.isOrgDataLoading) {
    return <LoadingScreen data-testid='loading-component' message='Loading…' />;
  }

  return <FlagsProvider features={togglePoints}>{children}</FlagsProvider>;
}
